import { QuestionGuidance } from "@repo/ui/components/question-guidance";
import { Badge } from "@repo/ui/components/ui/badge";
import { Ban, ChevronRight, CircleDashed, MinusCircle, Paperclip } from "lucide-react";
import { Fragment } from "react";
import type {
  AssessmentEntry,
  AssessmentOmission,
  AssessmentRow,
  AssessmentSection,
  AssessmentValue,
  ExamFindingState,
} from "../lib/assessment-detail-model";

const LIST_LIMIT = 6;

/** A finding with no result gets its own cue — never the destructive one, which means a required blank. */
const STATE_CUE: Record<ExamFindingState, { icon: typeof Ban; className: string }> = {
  declined: { icon: MinusCircle, className: "border-warning/40 bg-warning/10 text-warning" },
  blocked: { icon: Ban, className: "border-warning/40 bg-warning/10 text-warning" },
  pending: { icon: CircleDashed, className: "border-dashed border-border text-muted-foreground" },
};

/** One grid for the whole pane, so every section's rows share the same two column edges. */
export const PANE_GRID = "grid grid-cols-[minmax(9rem,15rem)_minmax(0,1fr)]";
export const LABEL_CELL = "py-1.5 pr-6";
export const VALUE_CELL = "min-w-0 py-1.5";
function omissionText({ labels, noun, phrase }: AssessmentOmission): string {
  const count = `${labels.length} ${labels.length === 1 ? noun : `${noun}s`} ${phrase}`;
  const shown = labels.slice(0, LIST_LIMIT).join(" · ");
  const rest = labels.length - LIST_LIMIT;
  return `${count}: ${shown}${rest > 0 ? ` +${rest} more` : ""}`;
}

export function AssessmentDetailBody({ sections }: { sections: readonly AssessmentSection[] }) {
  const recorded = sections.filter((section) => section.entries.length > 0);
  // A section nobody has touched earns a name behind a disclosure, not a heading
  // and a paragraph of its own — a long exam is mostly these.
  const untouched = sections.filter((section) => section.entries.length === 0);

  if (recorded.length === 0 && untouched.length === 0) {
    return <p className="text-sm text-muted-foreground">Nothing was recorded on this assessment.</p>;
  }

  return (
    <div className="max-w-3xl space-y-5">
      {recorded.length > 0 && (
        <div className={PANE_GRID}>
          {recorded.map((section) => (
            <Fragment key={section.sectionId}>
              <h3 className="col-span-2 mt-5 mb-1 flex items-baseline gap-2 text-sm font-semibold first:mt-0">
                {section.label}
                {section.tally.total > 0 && (
                  <span className="text-xs font-normal tabular-nums text-muted-foreground">
                    {section.tally.recorded} of {section.tally.total}
                  </span>
                )}
              </h3>
              {section.entries.map((entry) => (
                <Entry key={entry.entryId} entry={entry} />
              ))}
              {section.omitted && (
                <p className="col-span-2 pt-2 text-xs text-muted-foreground">{omissionText(section.omitted)}</p>
              )}
            </Fragment>
          ))}
        </div>
      )}

      {untouched.length > 0 && <UntouchedSections sections={untouched} />}
    </div>
  );
}

function UntouchedSections({ sections }: { sections: readonly AssessmentSection[] }) {
  const total = sections.reduce((sum, section) => sum + section.tally.total, 0);

  return (
    <details className="group border-t pt-3">
      <summary className="flex cursor-pointer list-none items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ChevronRight className="size-3 shrink-0 transition-transform group-open:rotate-90" />
        {sections.length} {sections.length === 1 ? "section" : "sections"} not yet charted
        {total > 0 && ` · ${total} items`}
      </summary>
      <ul className="mt-2 space-y-1 pl-4">
        {sections.map((section) => (
          <li key={section.sectionId} className="flex justify-between gap-4 text-xs text-muted-foreground">
            <span>{section.label}</span>
            <span className="tabular-nums">{section.tally.total}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}

function Entry({ entry }: { entry: AssessmentEntry }) {
  if (entry.kind === "display") {
    return <p className="col-span-2 py-1.5 text-sm text-muted-foreground">{entry.text}</p>;
  }
  if (entry.kind === "row") return <Row row={entry.row} />;

  return (
    <div className="col-span-2 py-1.5">
      <div className="mb-1.5 text-sm font-medium">{entry.label}</div>
      {entry.occurrences.map((occurrence) => (
        <div key={occurrence.occurrenceId} className="mt-1.5">
          <div className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">{occurrence.label}</div>
          <div className={PANE_GRID}>
            {occurrence.rows.map((row) => (
              <Row key={row.rowId} row={row} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Row({ row }: { row: AssessmentRow }) {
  // A note is the reason behind the answer, so it reads under it at prose width
  // rather than competing with it on the same line.
  const inline = row.values.filter((value) => value.kind !== "note");
  const notes = row.values.filter(
    (value): value is Extract<AssessmentValue, { kind: "note" }> => value.kind === "note",
  );

  return (
    <>
      <div className={`${LABEL_CELL} text-sm text-muted-foreground`}>
        {row.label}
        {row.badges.map((badge) => (
          <Badge key={badge} variant="outline" className="ml-1.5 px-1 py-0 align-middle text-[10px] font-normal">
            {badge}
          </Badge>
        ))}
      </div>
      <div className={VALUE_CELL}>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          {inline.map((value, index) => (
            <Value key={`${row.rowId}:${index}`} value={value} />
          ))}
          {row.link && (
            <a
              href={row.link.url}
              className="text-xs font-medium text-primary hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              {row.link.label}
            </a>
          )}
        </div>
        <QuestionGuidance help={row.help} options={row.options} />
        {notes.map((note, index) => (
          <p
            key={`${row.rowId}:note:${index}`}
            className="mt-1.5 whitespace-pre-wrap border-l-2 pl-2.5 text-sm text-muted-foreground"
          >
            {note.text}
          </p>
        ))}
      </div>
    </>
  );
}

function Value({ value }: { value: AssessmentValue }) {
  switch (value.kind) {
    case "chips":
      return (
        <span className="flex flex-wrap gap-1.5">
          {value.chips.map((chip) => (
            <Badge key={chip.label} variant="secondary" className="font-normal">
              {chip.label}
              {chip.points !== undefined && (
                <span className="tabular-nums text-muted-foreground">
                  · {chip.points} {chip.points === 1 ? "pt" : "pts"}
                </span>
              )}
            </Badge>
          ))}
        </span>
      );
    case "state": {
      const { icon: Icon, className } = STATE_CUE[value.state];
      return (
        <Badge variant="outline" className={`font-normal ${className}`}>
          <Icon />
          {value.text}
        </Badge>
      );
    }
    case "mono":
      return <span className="font-mono text-sm">{value.text}</span>;
    case "attachment":
      return (
        <a
          href={value.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex max-w-full items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <Paperclip className="size-3.5 shrink-0" />
          <span className="truncate">{value.label}</span>
        </a>
      );
    default:
      return (
        <Badge variant="outline" className="border-destructive/40 bg-destructive/10 font-normal text-destructive">
          {value.text}
        </Badge>
      );
  }
}
